import React, { FC, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

export type MiniTrendPoint = { date: string; value: number };

interface MiniTrendChartProps {
  data: MiniTrendPoint[];
  color: string;
  /**
   * `sparkline` — thinner stroke, lighter fill, tuned for dense tables (many instances per screen).
   * `default` — used on dashboard cards.
   */
  variant?: 'default' | 'sparkline';
}

const MiniTrendChart: FC<MiniTrendChartProps> = ({ data, color, variant = 'default' }) => {
  const option = useMemo(() => {
    const safe = data?.length ? data : [{ date: '', value: 0 }];
    const labels = safe.map(p => p.date);
    const values = safe.map(p => Number(p.value) || 0);
    const isSpark = variant === 'sparkline';

    return {
      animation: false,
      grid: isSpark
        ? { left: 0, right: 0, top: 0, bottom: 0, containLabel: false }
        : { left: 6, right: 4, top: 2, bottom: 2, containLabel: false },
      xAxis: { type: 'category' as const, data: labels, show: false, boundaryGap: false },
      yAxis: { type: 'value' as const, show: false, scale: true },
      tooltip: { show: false },
      series: [
        {
          type: 'line',
          data: values,
          smooth: isSpark ? 0.2 : 0.45,
          lineStyle: { color, width: isSpark ? 1.25 : 2 },
          areaStyle: isSpark ? { color: `${color}14` } : { color: `${color}26` },
          showSymbol: false,
          emphasis: { disabled: true },
        },
      ],
    };
  }, [color, data, variant]);

  return (
    <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge lazyUpdate />
  );
};

export default MiniTrendChart;
